import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";
import FloorMapViewer from "@/components/FloorMapViewer";
import DirectoryList from "@/components/DirectoryList";
import UnitDetailDrawer from "@/components/UnitDetailDrawer";
import { Map as MapIcon, List as ListIcon } from "lucide-react";

const FLOOR_TABS = [
  { id: "GF", wing: "", label: "Ground Floor", sublabel: "H-1 to H-23" },
  { id: "FF", wing: "R", label: "First Floor · Block A", sublabel: "H-24 to H-63" },
  { id: "FF", wing: "L", label: "First Floor · Block B", sublabel: "H-65A to H-83" },
];

export default function PublicDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [modalUnit, setModalUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mobile view: "map" or "list". Ignored on lg+ where both are shown.
  const [mobileView, setMobileView] = useState("map");

  const currentTab = FLOOR_TABS[activeTab];

  const fetchUnits = useCallback(async () => {
    setLoading(true);
    try {
      const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};
      const params = { floor: currentTab.id, wing: currentTab.wing };
      const { data } = await axios.get(`${API}/units`, { params, headers });
      setUnits(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, user]);

  useEffect(() => {
    fetchUnits();
    setSelectedUnit(null);
    setModalUnit(null);
  }, [fetchUnits]);

  const handleUnitClick = (unitId) => {
    setSelectedUnit(unitId);
    setModalUnit(unitId);
  };

  const handleTabChange = (idx) => {
    setActiveTab(idx);
    setSelectedUnit(null);
  };

  return (
    <div
      className="flex flex-col"
      style={{ height: "calc(100dvh - 64px)" }}
    >
      {/* Floor Tabs */}
      <div
        data-testid="floor-tabs"
        className="flex-shrink-0 border-b border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#1A1D24] px-3 sm:px-6"
      >
        <div className="flex items-end gap-0 overflow-x-auto no-scrollbar">
          {FLOOR_TABS.map((tab, idx) => (
            <button
              key={`${tab.id}-${tab.wing}`}
              data-testid={`floor-tab-${idx}`}
              onClick={() => handleTabChange(idx)}
              className={`flex flex-col items-start px-3 sm:px-5 py-3 border-b-2 transition-all duration-200 whitespace-nowrap ${
                activeTab === idx
                  ? "border-[#9C7C54] text-[#9C7C54]"
                  : "border-transparent text-[#737373] dark:text-[#A1A1AA] hover:text-[#1A1A1A] dark:hover:text-[#EAEAEA] hover:border-[#E5E5E5]"
              }`}
            >
              <span className="text-xs sm:text-sm font-semibold tracking-wide">{tab.label}</span>
              <span className="text-[10px] tracking-[0.12em] uppercase opacity-70">{tab.sublabel}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Map/List switcher — hidden on lg+ */}
      <div
        className="lg:hidden flex-shrink-0 flex items-center justify-center gap-1 px-3 py-2 border-b border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#1A1D24]"
        data-testid="mobile-view-switch"
      >
        <div className="inline-flex bg-[#F5F3F0] dark:bg-[#0F1115] rounded-sm p-0.5">
          <button
            data-testid="mobile-view-map"
            onClick={() => setMobileView("map")}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold tracking-wide rounded-sm transition-colors ${
              mobileView === "map"
                ? "bg-[#9C7C54] text-white"
                : "text-[#737373] dark:text-[#A1A1AA]"
            }`}
          >
            <MapIcon size={13} /> Map
          </button>
          <button
            data-testid="mobile-view-list"
            onClick={() => setMobileView("list")}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold tracking-wide rounded-sm transition-colors ${
              mobileView === "list"
                ? "bg-[#9C7C54] text-white"
                : "text-[#737373] dark:text-[#A1A1AA]"
            }`}
          >
            <ListIcon size={13} /> List
            <span className="ml-1 text-[10px] opacity-80">({units.length})</span>
          </button>
        </div>
      </div>

      {/* Split / Stacked Layout */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
        {/* Floor Map */}
        <div
          data-testid="floor-map-section"
          className={`${
            mobileView === "map" ? "flex" : "hidden"
          } lg:flex w-full lg:w-3/5 xl:w-2/3 bg-[#F5F4F2] dark:bg-[#0F1115] lg:border-r border-[#E5E5E5] dark:border-[#272A30] overflow-hidden flex-1 min-h-0`}
        >
          <FloorMapViewer
            floor={currentTab.id}
            wing={currentTab.wing}
            units={units}
            selectedUnit={selectedUnit}
            onUnitClick={handleUnitClick}
          />
        </div>

        {/* Directory List */}
        <div
          data-testid="directory-section"
          className={`${
            mobileView === "list" ? "flex" : "hidden"
          } lg:flex flex-1 lg:w-2/5 xl:w-1/3 bg-white dark:bg-[#1A1D24] overflow-hidden flex-col min-h-0`}
        >
          <DirectoryList
            units={units}
            loading={loading}
            selectedUnit={selectedUnit}
            onUnitSelect={handleUnitClick}
            floor={currentTab.id}
            wing={currentTab.wing}
          />
        </div>
      </div>

      {/* Unit Detail Drawer */}
      {modalUnit && (
        <UnitDetailDrawer
          unitId={modalUnit}
          onClose={() => setModalUnit(null)}
        />
      )}
    </div>
  );
}
